import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FundOutlined
} from '@ant-design/icons';
import { apiService } from '../../../services/api';
import { AdPosition } from '../../../types';
import './Ads.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const Ads: React.FC = () => {
  const [adPositions, setAdPositions] = useState<AdPosition[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAd, setEditingAd] = useState<AdPosition | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadAdPositions();
  }, []);

  const loadAdPositions = async () => {
    setLoading(true);
    try {
      const response = await apiService.adminGetAdPositions();
      if (response.code === 0) {
        setAdPositions(response.data);
      }
    } catch (error) {
      message.error('Failed to load ad positions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAd(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: AdPosition) => {
    setEditingAd(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      header_code: record.header_code,
      description: record.description,
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.adminDeleteAdPosition(id);
      message.success('Deleted successfully');
      loadAdPositions();
    } catch (error) {
      message.error('Delete failed');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingAd) {
        await apiService.adminUpdateAdPosition(editingAd.id, values);
        message.success('Updated successfully');
      } else {
        await apiService.adminCreateAdPosition(values);
        message.success('Created successfully');
      }
      setModalVisible(false);
      loadAdPositions();
    } catch (error) {
      message.error(editingAd ? 'Update failed' : 'Create failed');
    }
  };

  const handleInitialize = async () => {
    try {
      await apiService.adminInitializeAdPositions();
      message.success('Default ad positions initialized successfully');
      loadAdPositions();
    } catch (error) {
      message.error('Initialization failed');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Text strong>{text}</Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Ad Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <Text type="secondary">
          {code ? `${code.substring(0, 50)}...` : 'Not Set'}
        </Text>
      ),
    },
    {
      title: 'Header Code',
      dataIndex: 'header_code',
      key: 'header_code',
      render: (headerCode: string) => (
        <Text type="secondary">
          {headerCode ? `${headerCode.substring(0, 50)}...` : 'Not Set'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 200,
      render: (_: any, record: AdPosition) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this ad position?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="ads-container">
      <div className="page-header">
        <Title level={2}>
          <FundOutlined /> Ad Management
        </Title>
        <Text type="secondary">
          Manage ad positions and ad codes for different pages
        </Text>
      </div>

      <Alert
        message="Ad Position Information"
        description="The system has default page ad positions plus Full Site Ad. Use full_site_ad for header-only code that should load once across all public pages, such as Google Ad Manager web interstitial ads."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card>
        <div className="table-header">
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add Ad Position
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadAdPositions}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              onClick={handleInitialize}
            >
              Initialize Default Ads
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={adPositions}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingAd ? 'Edit Ad Position' : 'Add Ad Position'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Ad Position Name"
            rules={[{ required: true, message: 'Please enter ad position name' }]}
          >
            <Input placeholder="e.g., homepage_ad" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input placeholder="e.g., Homepage top banner ad" />
          </Form.Item>

          <Form.Item
            name="code"
            label="Ad Code"
          >
            <TextArea
              rows={6}
              placeholder="Enter Google AdSense code or other ad code"
            />
          </Form.Item>

          <Form.Item
            name="header_code"
            label="Header Code"
          >
            <TextArea
              rows={4}
              placeholder="Code to be appended into the <head> for pages using this ad position (e.g., extra AdSense scripts)"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active Status"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingAd ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Ads; 
